import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Comment, NewComment } from '../types';

export async function fetchComments(postId: string): Promise<Comment[]> {
  const commentsRef = collection(db, 'comments');

  // Fetch all comments for this post
  const q = query(
    commentsRef,
    where('postId', '==', postId),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);

  // Build a map of all comments
  const commentMap = new Map<string, Comment>();
  const allComments: Comment[] = [];

  for (const docSnap of snapshot.docs) {
    const commentData = docSnap.data();
    const comment: Comment = {
      id: docSnap.id,
      comment: commentData.comment,
      postId: commentData.postId,
      userId: commentData.userId,
      parentId: commentData.parentId || null,
      createdAt: commentData.createdAt,
      replies: [],
    };
    commentMap.set(docSnap.id, comment);
    allComments.push(comment);
  }

  // Organize into tree structure
  const topLevelComments: Comment[] = [];

  for (const comment of allComments) {
    if (!comment.parentId) {
      // Top-level comment
      topLevelComments.push(comment);
    } else {
      // Reply - add to parent's replies
      const parent = commentMap.get(comment.parentId);
      if (parent) {
        parent.replies = parent.replies || [];
        parent.replies.push(comment);
      } else {
        // Parent not found, treat as top-level
        topLevelComments.push(comment);
      }
    }
  }

  return topLevelComments;
}

export async function insertComment(
  newComment: NewComment,
  userId: string
): Promise<string> {
  const docRef = await addDoc(collection(db, 'comments'), {
    ...newComment,
    parentId: newComment.parentId || null,
    userId,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function deleteComment(id: string): Promise<void> {
  await deleteDoc(doc(db, 'comments', id));
}
